import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSFormControl from "views/system-design/DSFormControl";
import DSInputLabel from "views/system-design/DSInputLabel";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSCardHeader from "views/system-design/DSCardHeader";
import DSChip from "views/system-design/DSChip";
import DSAccordion from "views/system-design/DSAccordion";
import DSAccordionSummary from "views/system-design/DSAccordionSummary";
import DSAccordionDetails from "views/system-design/DSAccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { versionTesterApi } from "../api/versionTesterApi";
import type { AnalysisIssue, StoryResult } from "../types";
import StoryResultsPanel from "./StoryResultsPanel";

const severityColor = (severity: AnalysisIssue["severity"]) => {
  switch (severity) {
    case "critical":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "default";
  }
};

/**
 * AnalysisHistory - displays saved AI analysis results per version.
 *
 * Shows a dropdown of tested versions and renders the AI analysis
 * for the selected version, including verdict, summary, and issues.
 */
const AnalysisHistory: React.FC = () => {
  const [selectedVersion, setSelectedVersion] = useState<string>("");

  // Fetch list of available versions
  const { data: analysisList, isLoading: listLoading } = useQuery({
    queryKey: ["analysisList"],
    queryFn: () => versionTesterApi.getAnalysisList(),
    refetchInterval: 10000,
    retry: 1,
  });

  // Fetch analysis for selected version
  const { data: analysis, isLoading: analysisLoading } = useQuery({
    queryKey: ["analysis", selectedVersion],
    queryFn: () => versionTesterApi.getAnalysis(selectedVersion),
    enabled: !!selectedVersion,
    retry: 1,
  });

  const versions = analysisList?.versions ?? [];

  if (listLoading) {
    return (
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
        <DSCircularProgress size={20} />
        <DSTypography variant="body2" color="text.secondary">
          Loading analysis history...
        </DSTypography>
      </DSBox>
    );
  }

  if (versions.length === 0) {
    return (
      <DSBox sx={{ mt: 2 }}>
        <DSTypography variant="body2" color="text.secondary">
          No saved analyses yet. Run a test to generate one.
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={{ mt: 2 }}>
      {/* Version dropdown */}
      <DSFormControl sx={{ minWidth: 200 }}>
        <DSInputLabel id="analysis-version-label">Version</DSInputLabel>
        <DSSelect
          labelId="analysis-version-label"
          value={selectedVersion}
          onChange={(e) => setSelectedVersion(e.target.value as string)}
          label="Version"
        >
          {versions.map((version) => (
            <DSMenuItem key={version} value={version}>
              {version}
            </DSMenuItem>
          ))}
        </DSSelect>
      </DSFormControl>

      {/* Loading state */}
      {analysisLoading && selectedVersion && (
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <DSCircularProgress size={20} />
          <DSTypography variant="body2">Loading analysis...</DSTypography>
        </DSBox>
      )}

      {/* Analysis results */}
      {analysis && !analysisLoading && (
        <DSBox sx={{ mt: 3 }}>
          {/* Overall status */}
          <DSBox sx={{ mb: 2 }}>
            <DSChip
              label={analysis.overallStatus === "passed" ? "PASSED" : "FAILED"}
              color={analysis.overallStatus === "passed" ? "success" : "error"}
              size="medium"
            />
            <DSTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Version {analysis.config.targetVersion} &mdash;{" "}
              {analysis.completedAt
                ? new Date(analysis.completedAt).toLocaleString()
                : "In progress"}
            </DSTypography>
          </DSBox>

          {/* Per-device AI analysis */}
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {analysis.deviceResults.map((deviceResult) => (
              <DSCard key={deviceResult.deviceId}>
                <DSCardHeader
                  title={deviceResult.deviceId}
                  action={
                    deviceResult.aiAnalysis ? (
                      <DSChip
                        label={
                          deviceResult.aiAnalysis.verdict === "pass"
                            ? "PASS"
                            : "FAIL"
                        }
                        color={
                          deviceResult.aiAnalysis.verdict === "pass"
                            ? "success"
                            : "error"
                        }
                        size="small"
                      />
                    ) : (
                      <DSChip label={deviceResult.status} size="small" />
                    )
                  }
                />
                <DSCardContent>
                  {deviceResult.aiAnalysis ? (
                    <DSBox
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* AI Summary */}
                      <DSTypography variant="body1">
                        {deviceResult.aiAnalysis.summary}
                      </DSTypography>

                      <DSTypography variant="body2" color="text.secondary">
                        {deviceResult.aiAnalysis.logEntriesAnalyzed} log entries
                        analyzed
                      </DSTypography>

                      {/* Issues */}
                      {deviceResult.aiAnalysis.issues.length > 0 ? (
                        <DSBox>
                          {deviceResult.aiAnalysis.issues.map(
                            (issue, index) => (
                              <DSAccordion key={`${issue.category}-${index}`}>
                                <DSAccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                >
                                  <DSBox
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <DSChip
                                      label={issue.severity}
                                      color={
                                        severityColor(issue.severity) as
                                          | "error"
                                          | "warning"
                                          | "info"
                                          | "default"
                                      }
                                      size="small"
                                    />
                                    <DSTypography>
                                      {issue.category}
                                    </DSTypography>
                                  </DSBox>
                                </DSAccordionSummary>
                                <DSAccordionDetails>
                                  <DSTypography variant="body2" sx={{ mb: 1 }}>
                                    {issue.description}
                                  </DSTypography>
                                  {issue.logExcerpts.length > 0 && (
                                    <DSBox
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                      }}
                                    >
                                      {issue.logExcerpts.map((excerpt, i) => (
                                        <DSTypography
                                          key={i}
                                          variant="body2"
                                          sx={{
                                            fontFamily: "monospace",
                                            fontSize: "0.85rem",
                                            wordBreak: "break-word",
                                            bgcolor: "grey.100",
                                            p: 0.5,
                                            borderRadius: 1,
                                          }}
                                        >
                                          {excerpt}
                                        </DSTypography>
                                      ))}
                                    </DSBox>
                                  )}
                                </DSAccordionDetails>
                              </DSAccordion>
                            ),
                          )}
                        </DSBox>
                      ) : (
                        <DSTypography
                          variant="body2"
                          sx={{ color: "success.main" }}
                        >
                          No issues detected
                        </DSTypography>
                      )}

                      {/* Story results section */}
                      {deviceResult.storyResults &&
                        deviceResult.storyResults.length > 0 && (
                          <StoryResultsPanel
                            storyResults={deviceResult.storyResults}
                          />
                        )}
                    </DSBox>
                  ) : (
                    <DSTypography variant="body2" color="text.secondary">
                      No AI analysis available for this device
                    </DSTypography>
                  )}
                </DSCardContent>
              </DSCard>
            ))}
          </DSBox>
        </DSBox>
      )}
    </DSBox>
  );
};

export default AnalysisHistory;
